import moment, { Moment } from 'moment'
import { useState, useCallback } from 'react'
import { PresetType } from '../types'
import {
	getTodayRange,
	getYesterdayRange,
	getWeekRange,
	getMonthRange,
	getYearRange,
	getFutureWeekRange,
	getFutureMonthRange,
	getFutureYearRange,
	applyDayOffset,
} from '../utils/dateCalculations'

interface UsePresetsReturn {
	activePreset: PresetType | null
	selectPreset: (preset: PresetType) => void
	clearPreset: () => void
}

/**
 * Хук для управления пресетами (быстрыми периодами)
 * Обрабатывает выбор Today, Yesterday, Week, Month, Year
 */
export const usePresets = (
	onRangeChange: (startDate: Moment, endDate: Moment) => void,
	isNeedFutureDates?: boolean,
	dayBefore?: number,
): UsePresetsReturn => {
	const [activePreset, setActivePreset] = useState<PresetType | null>(null)

	const selectPreset = useCallback(
		(preset: PresetType) => {
			let range: { startDate: number; endDate: number }

			switch (preset) {
				case 'today':
					range = getTodayRange()
					break
				case 'yesterday':
					range = getYesterdayRange()
					break
				case 'week':
					range = isNeedFutureDates ? getFutureWeekRange() : getWeekRange()
					break
				case 'month':
					range = isNeedFutureDates ? getFutureMonthRange() : getMonthRange()
					break
				case 'year':
					range = isNeedFutureDates ? getFutureYearRange() : getYearRange()
					break
				default:
					return
			}

			// Применяем dayBefore offset если указан
			if (dayBefore) {
				range = applyDayOffset(range, dayBefore)
			}

			setActivePreset(preset)
			onRangeChange(moment(range.startDate), moment(range.endDate))
		},
		[onRangeChange, isNeedFutureDates, dayBefore],
	)

	const clearPreset = useCallback(() => {
		setActivePreset(null)
	}, [])

	return {
		activePreset,
		selectPreset,
		clearPreset,
	}
}
